import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ChevronLeft, Sparkles } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { track } from "../lib/track";

const PUBLIC_DOMAIN = "https://moodcard.app";

// iter-93 — public remix feed for a parent card.
// Renders up to 30 child remixes with a slim preview each.
export default function RemixFeed() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [parent, setParent] = useState(null);
    const [remixes, setRemixes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/public/c/${slug}/remixes`);
                if (!cancelled) {
                    setParent(data?.parent || null);
                    setRemixes(data?.remixes || []);
                    track("remix_feed_viewed", { slug, count: data?.count });
                }
            } catch (e) {
                if (!cancelled) setError("couldn't load the remix tree.");
                console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    const url = `${PUBLIC_DOMAIN}/c/${slug}/remixes`;

    return (
        <div className="relative min-h-screen w-full bg-app overflow-x-hidden" data-testid="remix-feed-screen">
            <Helmet>
                <title>Remixes of this mood — MoodCard</title>
                <link rel="canonical" href={url} />
                <meta name="description" content="See how others felt this. Tap any remix to read it, or make your own." />
            </Helmet>

            <div className="absolute inset-0 aurora-wash opacity-25 pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />

            <header className="relative z-10 max-w-md mx-auto px-5 pt-5 flex items-center justify-between">
                <button
                    onClick={() => navigate(`/c/${slug}`)}
                    data-testid="remix-feed-back"
                    className="flex items-center gap-1.5 font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 hover:text-app transition-colors"
                >
                    <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.6} />
                    back
                </button>
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                    the tree
                </span>
            </header>

            <main className="relative z-10 max-w-md mx-auto px-5 pt-6 pb-24">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-2">
                        {remixes.length} {remixes.length === 1 ? "remix" : "remixes"}
                    </p>
                    <h1 className="font-serif font-light italic text-app text-2xl leading-tight tracking-tight">
                        ways others felt this.
                    </h1>
                    {parent?.feeling && (
                        <p className="mt-3 font-serif italic text-app-3 text-sm leading-relaxed">
                            inspired by: <span className="text-app-2">"{parent.feeling.slice(0, 100)}{parent.feeling.length > 100 ? "…" : ""}"</span>
                        </p>
                    )}
                </motion.div>

                {loading && (
                    <p className="mt-10 text-center font-serif italic text-[12px] text-app-3">
                        gathering the tree…
                    </p>
                )}

                {error && (
                    <p className="mt-10 text-center font-serif italic text-[13px] text-rose-200/80">
                        {error}
                    </p>
                )}

                {!loading && !error && remixes.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="mt-10 text-center"
                    >
                        <p className="font-serif italic text-app-2 text-lg leading-relaxed">
                            no one's remixed this yet.
                        </p>
                        <p className="mt-2 font-serif italic text-app-3 text-[13px]">
                            be the first to make a version.
                        </p>
                        <button
                            onClick={() => navigate(`/remix/${slug}`)}
                            data-testid="remix-feed-make-first"
                            className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-cta text-on-app font-sans text-sm font-medium tracking-wide"
                        >
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                            make my version
                        </button>
                    </motion.div>
                )}

                {!loading && remixes.length > 0 && (
                    <div className="mt-7 space-y-3">
                        {remixes.map((r, idx) => (
                            <motion.div
                                key={r.id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.45, delay: Math.min(idx * 0.04, 0.4) }}
                            >
                                <Link
                                    to={`/c/${r.slug}`}
                                    data-testid={`remix-tile-${r.slug || idx}`}
                                    className="block rounded-2xl border border-app/20 bg-surface-1 backdrop-blur-md px-4 py-3.5 hover:border-app/40 transition-colors"
                                >
                                    <p className="font-sans text-[9px] uppercase tracking-[0.32em] text-app-3">
                                        {(r.pack || "").replace("_", " ")}
                                    </p>
                                    {r.preview?.title && (
                                        <p className="mt-1.5 font-serif font-medium text-app text-base leading-tight">
                                            {r.preview.title}
                                        </p>
                                    )}
                                    {r.preview?.body && (
                                        <p className="mt-1.5 font-serif italic text-app-2 text-[13px] leading-snug line-clamp-3">
                                            {r.preview.body}
                                        </p>
                                    )}
                                </Link>
                            </motion.div>
                        ))}
                        <div className="pt-4">
                            <button
                                onClick={() => navigate(`/remix/${slug}`)}
                                data-testid="remix-feed-make-mine"
                                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-cta text-on-app font-sans text-sm font-medium tracking-wide"
                            >
                                <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                                make my version
                            </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
